// 🔧 logs.js
// ✅ Purpose: Admin-only page for viewing and managing error logs from users
// ✅ Features:
// - Loads logs from Firestore
// - Filters by log level and date range
// - Paginated display
// - Export to XLSX
// - Delete individual or all logs
// - Styled with admin template layout

import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  deleteDoc,
  doc,
  getDocs,
  limit,
} from "firebase/firestore";
import { db } from "../../firebase";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import FullPageSpinner from "../../components/FullPageSpinner";
import { showStatus } from "../../utils/status_helpers";

const AdminLogs = () => {

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingAll, setDeletingAll] = useState(false);
  const [status, setStatus] = useState(null);
  const [levelFilter, setLevelFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const logsPerPage = 10;

  useEffect(() => {
    if (!status) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  useEffect(() => {
    
    const q = query(
      collection(db, "logs"),
      orderBy("timestamp", "desc"),
      limit(1000)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setLogs(data);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [levelFilter, startDate, endDate]);

  const deleteLog = async (id) => {
    if (!window.confirm("Delete this log?")) return;

    try {
      await deleteDoc(doc(db, "logs", id));

      showStatus(setStatus, {
        type: "success",
        message: "Log deleted successfully.",
      });
    } catch (err) {
      console.error("Failed to delete log:", err);

      showStatus(setStatus, {
        type: "error",
        message: "Failed to delete log.",
      });
    }
  };

  const deleteAllLogs = async () => {
    if (!window.confirm("Delete ALL logs? This cannot be undone.")) return;

    setDeletingAll(true);

    try {
      const snapshot = await getDocs(collection(db, "logs"));
      const batch = snapshot.docs.map((docSnap) => deleteDoc(docSnap.ref));

      await Promise.all(batch);

      showStatus(setStatus, {
        type: "warning",
        message: "All logs deleted.",
      });
    } catch (err) {
      console.error("Failed to delete all logs:", err);

      showStatus(setStatus, {
        type: "error",
        message: "Failed to delete all logs.",
      });
    } finally {
      setDeletingAll(false);
    }
  };

  const exportToXLSX = () => {
    const exportData = filteredLogs.map((log) => ({
      Timestamp: log.timestamp?.toDate().toISOString(),
      Level: log.level,
      Message: log.message,
      ErrorCode: log.errorCode || "",
      ErrorName: log.errorName || "",
      ErrorMessage: log.errorMessage || "",
      Environment: log.environment || "",
      Online: typeof log.online === "boolean" ? String(log.online) : "",
      Context: JSON.stringify(log.context),
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Logs");
    const blob = XLSX.write(workbook, { type: "binary", bookType: "xlsx" });
    const buffer = new ArrayBuffer(blob.length);
    const view = new Uint8Array(buffer);
    for (let i = 0; i < blob.length; i++) {
      view[i] = blob.charCodeAt(i) & 0xff;
    }
    saveAs(new Blob([buffer], { type: "application/octet-stream" }), "logs.xlsx");
  };

  const filteredLogs = logs.filter((log) => {
    const ts = log.timestamp?.toDate();
    const matchLevel = levelFilter === "all" || log.level === levelFilter;
    const matchStart = !startDate || (ts && ts >= new Date(startDate));
    const matchEnd = !endDate || (ts && ts <= new Date(endDate + "T23:59"));
    return matchLevel && matchStart && matchEnd;
  });

  const pageCount = Math.ceil(filteredLogs.length / logsPerPage);
  const paginatedLogs = filteredLogs.slice((currentPage - 1) * logsPerPage, currentPage * logsPerPage);

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="row mb-4">
        <div className="col text-start">
          <h1 className="display-5">🪵 Error Logs</h1>
          <p className="lead text-muted">View and manage log entries reported by users</p>
        </div>
      </div>

      <div className="white-section p-4 rounded shadow-sm bg-white">

        {status?.message && (
          <div
            className={`alert ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
          >
            {status.message}
          </div>
        )}

        <div className="row mb-3">
          <div className="col-md-2">
            <label className="form-label">Level</label>
            <select className="form-select" value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="error">Error</option>
              <option value="warn">Warn</option>
              <option value="info">Info</option>
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label">Start Date</label>
            <input type="date" className="form-control" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div className="col-md-2">
            <label className="form-label">End Date</label>
            <input type="date" className="form-control" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <div className="col-md-6 text-end d-flex align-items-end justify-content-end gap-2">
            <button className="btn btn-outline-secondary" onClick={exportToXLSX}>
              📄 Export to XLSX
            </button>
            <button className="btn btn-danger" onClick={deleteAllLogs} disabled={deletingAll}>
              {deletingAll ? "Deleting..." : "🗑️ Delete All Logs"}
            </button>
          </div>
        </div>

        {loading ? (
          <FullPageSpinner message="Loading Logs..." />
        ) : filteredLogs.length === 0 ? (
          <div className="alert alert-info">No logs found for selected filters.</div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-sm table-bordered align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Time</th>
                    <th>Level</th>
                    <th>Message</th>
                    <th>Diagnostic</th>
                    <th>Context</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedLogs.map((log) => (
                    <tr key={log.id}>
                      <td>{log.timestamp?.toDate().toLocaleString()}</td>
                      <td className="text-uppercase">{log.level}</td>
                      <td style={{ maxWidth: 200 }}>{log.message}</td>
                      <td style={{ maxWidth: 220 }}>
                        <div><strong>Code:</strong> {log.errorCode || "—"}</div>
                        <div><strong>Name:</strong> {log.errorName || "—"}</div>
                        <div><strong>Environment:</strong> {log.environment || "—"}</div>
                        <div><strong>Online:</strong> {typeof log.online === "boolean" ? String(log.online) : "—"}</div>
                        {log.errorMessage && <div><strong>Detail:</strong> {log.errorMessage}</div>}
                      </td>
                      <td>
                        <pre className="mb-0" style={{ whiteSpace: "pre-wrap", maxWidth: 300 }}>
                          {JSON.stringify(log.context, null, 2)}
                        </pre>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => deleteLog(log.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <nav className="mt-3">
              <ul className="pagination">
                {Array.from({ length: pageCount }, (_, i) => (
                  <li key={i} className={`page-item ${i + 1 === currentPage ? "active" : ""}`}>
                    <button className="page-link" onClick={() => setCurrentPage(i + 1)}>
                      {i + 1}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </>
        )}
      </div>
    </section>
  );
};

export default AdminLogs;
